import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { PageContainer, PageHeader, Card } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function OnlineStoreSettings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["online-store-settings"],
    queryFn: async () => {
      const { data: res } = await api.get("/online-store/settings");
      return res;
    },
  });

  const { data: counters } = useQuery({
    queryKey: ["counters-online-settings"],
    queryFn: async () => {
      const { data: res } = await api.get("/counters");
      return res.counters || [];
    },
  });

  useEffect(() => {
    if (data?.settings) setForm({ ...data.settings });
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.put("/online-store/settings", payload);
      return res;
    },
    onSuccess: (res) => {
      toast.success("Settings saved");
      setForm(res.settings);
      qc.setQueryData(["online-store-settings"], res);
    },
    onError: (e) => toast.error(e.response?.data?.message || "Save failed"),
  });

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  }

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="py-16 flex justify-center">
          <Loader2 className="animate-spin text-brand-orange" />
        </div>
      </PageContainer>
    );
  }

  const storeUrl = `${window.location.origin}/s/${form.branch_code}`;
  const callUrl = `${storeUrl}?source=call`;

  return (
    <PageContainer>
      <PageHeader
        title="Online store"
        subtitle="Public menu, WhatsApp checkout, and call-order link"
        actions={
          <Button
            className="bg-brand-orange hover:bg-[#E05E10]"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate(form)}
          >
            {saveMut.isPending ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
            Save
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4 space-y-4">
          <label className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-brand-brown">Enable online store</div>
              <div className="text-[12px] text-slate-500">Public catalog at /s/{form.branch_code}</div>
            </div>
            <Switch checked={!!form.is_enabled} onCheckedChange={(v) => setField("is_enabled", v)} />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-medium">Supports pickup</span>
            <Switch checked={!!form.supports_pickup} onCheckedChange={(v) => setField("supports_pickup", v)} />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-[13px] font-medium">Supports delivery</span>
            <Switch checked={!!form.supports_delivery} onCheckedChange={(v) => setField("supports_delivery", v)} />
          </label>

          <div>
            <Label>WhatsApp number</Label>
            <Input className="mt-1" value={form.whatsapp_number || ""} onChange={(e) => setField("whatsapp_number", e.target.value)} placeholder="9715…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Min order</Label>
              <Input className="mt-1" type="number" value={form.min_order_amount ?? 0} onChange={(e) => setField("min_order_amount", Number(e.target.value))} />
            </div>
            <div>
              <Label>Delivery fee</Label>
              <Input className="mt-1" type="number" value={form.delivery_fee ?? 0} onChange={(e) => setField("delivery_fee", Number(e.target.value))} />
            </div>
          </div>
          <div>
            <Label>Free delivery above (optional)</Label>
            <Input
              className="mt-1"
              type="number"
              value={form.free_delivery_above ?? ""}
              onChange={(e) => setField("free_delivery_above", e.target.value === "" ? null : Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Default counter for Convert to KOT</Label>
            <Select
              value={form.default_counter_id ? String(form.default_counter_id) : "none"}
              onValueChange={(v) => setField("default_counter_id", v === "none" ? null : Number(v))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select counter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">First active counter</SelectItem>
                {(counters || []).map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Hero title</Label>
            <Input className="mt-1" value={form.hero_title || ""} onChange={(e) => setField("hero_title", e.target.value)} />
          </div>
          <div>
            <Label>Hero subtitle</Label>
            <Input className="mt-1" value={form.hero_subtitle || ""} onChange={(e) => setField("hero_subtitle", e.target.value)} />
          </div>
        </Card>

        <Card className="p-4 space-y-4">
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Customer store link</div>
            <div className="flex gap-2">
              <Input readOnly value={storeUrl} className="text-[12px]" />
              <Button type="button" variant="outline" size="icon" onClick={() => copy(storeUrl)}>
                <Copy size={14} />
              </Button>
              <Button type="button" variant="outline" size="icon" asChild>
                <a href={storeUrl} target="_blank" rel="noreferrer">
                  <ExternalLink size={14} />
                </a>
              </Button>
            </div>
          </div>
          <div>
            <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Call order link</div>
            <p className="text-[11px] text-slate-500 mb-1">Staff use this while on the phone — orders show under Call filter.</p>
            <div className="flex gap-2">
              <Input readOnly value={callUrl} className="text-[12px]" />
              <Button type="button" variant="outline" size="icon" onClick={() => copy(callUrl)}>
                <Copy size={14} />
              </Button>
              <Button type="button" variant="outline" size="icon" asChild>
                <a href={callUrl} target="_blank" rel="noreferrer">
                  <ExternalLink size={14} />
                </a>
              </Button>
            </div>
          </div>
          <div className="rounded-xl bg-brand-cream-panel border border-brand-cream-border p-3 text-[12px] text-brand-brown/80">
            Mark menu items with <strong>Show on online catalog</strong> in Menu Item Master. Orders never hit KOT until you click <strong>Convert to KOT</strong> in the inbox.
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
